package com.daytimegaming.ourdailyfinances.data.repository

import com.daytimegaming.ourdailyfinances.data.network.service.AccountService
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.domain.model.RecurringTransactionStream
import com.daytimegaming.ourdailyfinances.domain.model.Investments
import com.daytimegaming.ourdailyfinances.domain.model.PortfolioHistory
import com.daytimegaming.ourdailyfinances.domain.model.SecurityPerformance
import com.daytimegaming.ourdailyfinances.domain.model.PlaidPricesSync
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

class TransactionRepositoryImpl(private val service: AccountService) : TransactionRepository {

    private val _transactions = MutableStateFlow<Response<List<Transaction>>>(Response.Loading)
    override val transactions: StateFlow<Response<List<Transaction>>> = _transactions.asStateFlow()

    private val _recurringTransactions = MutableStateFlow<Response<List<RecurringTransactionStream>>>(Response.Loading)
    override val recurringTransactions: StateFlow<Response<List<RecurringTransactionStream>>> = _recurringTransactions.asStateFlow()

    private val _investments = MutableStateFlow<Response<Investments?>>(Response.Loading)
    override val investments: StateFlow<Response<Investments?>> = _investments.asStateFlow()

    private val _portfolioHistory = MutableStateFlow<Response<PortfolioHistory?>>(Response.Loading)
    override val portfolioHistory: StateFlow<Response<PortfolioHistory?>> = _portfolioHistory.asStateFlow()

    override suspend fun refresh() {
        if (_transactions.value !is Response.Success) {
            _transactions.value = Response.Loading
        }
        if (_investments.value !is Response.Success) {
            _investments.value = Response.Loading
        }
        try {
            val response = service.getTransactions()
            val list = response.transactions.map { it.toDomain() }
            _transactions.value = Response.Success(list)
            _investments.value = Response.Success(response.investments?.toDomain())
        } catch (e: Exception) {
            _transactions.value = Response.Error(e.message ?: "Failed to load transactions")
            _investments.value = Response.Error(e.message ?: "Failed to load investments")
        }
    }

    override suspend fun refreshRecurringTransactions() {
        if (_recurringTransactions.value !is Response.Success) {
            _recurringTransactions.value = Response.Loading
        }
        try {
            val list = service.getRecurringTransactions().recurringStreams.map { it.toDomain() }
            _recurringTransactions.value = Response.Success(list)
        } catch (e: Exception) {
            _recurringTransactions.value = Response.Error(e.message ?: "Failed to load recurring transactions")
        }
    }

    override suspend fun updateTransactionCategory(transactionId: String, detailed: String?): Response<Transaction> {
        return try {
            val updatedDto = service.updateTransactionCategory(transactionId, detailed)
            val updatedTx = updatedDto.toDomain()
            val currentResponse = _transactions.value
            if (currentResponse is Response.Success) {
                val updatedList = currentResponse.data.map {
                    if (it.transactionId == transactionId) updatedTx else it
                }
                _transactions.value = Response.Success(updatedList)
            }
            Response.Success(updatedTx)
        } catch (e: Exception) {
            Response.Error(e.message ?: "Failed to update category")
        }
     }

    override suspend fun refreshPortfolioHistory() {
        if (_portfolioHistory.value !is Response.Success) {
            _portfolioHistory.value = Response.Loading
        }
        try {
            val history = service.getPortfolioHistory().toDomain()
            _portfolioHistory.value = Response.Success(history)
        } catch (e: Exception) {
            _portfolioHistory.value = Response.Error(e.message ?: "Failed to load portfolio history")
        }
    }

    override suspend fun getSecurityPerformance(
        dashboardId: String?,
        accountId: String?,
    ): Response<SecurityPerformance> {
        return try {
            val response = service.getSecurityPerformance(dashboardId, accountId)
            Response.Success(response.toDomain())
        } catch (e: Exception) {
            Response.Error(e.message ?: "Failed to load security performance")
        }
    }

    override suspend fun syncInvestmentPrices(source: String): Response<PlaidPricesSync> {
        return try {
            val response = service.syncInvestmentPrices(source)
            Response.Success(response.toDomain())
        } catch (e: Exception) {
            Response.Error(e.message ?: "Failed to sync investment prices")
        }
    }
}
